
import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { storiesService } from "@/services/storiesService";

interface StoryVideoUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: (storyId: string) => void;
  userLocation: { lat: number; lng: number } | null;
}

const StoryVideoUpload = ({ isOpen, onClose, onUploadSuccess, userLocation }: StoryVideoUploadProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateVideo = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      setValidationError(null);
      
      // Check file size (1GB max)
      if (file.size > 1024 * 1024 * 1024) {
        setValidationError("File size must be less than 1GB");
        resolve(false);
        return;
      }

      // Check file type
      if (!file.type.startsWith('video/')) {
        setValidationError("Please select a video file");
        resolve(false);
        return;
      }

      // Check duration and resolution
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        if (video.duration > 15) {
          setValidationError("Video must be 15 seconds or shorter");
          resolve(false);
          return;
        }

        // Check aspect ratio (should be 9:16 or close to it)
        const aspectRatio = video.videoWidth / video.videoHeight;
        if (aspectRatio < 0.5 || aspectRatio > 0.6) {
          setValidationError("Video should have a 9:16 aspect ratio (vertical)");
          resolve(false);
          return;
        }

        resolve(true);
      };

      video.onerror = () => {
        setValidationError("Invalid video file");
        resolve(false);
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isValid = await validateVideo(file);
    if (isValid) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !userLocation) {
      toast({
        title: "Error",
        description: "Please select a video and allow location access",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    
    try {
      const story = await storiesService.uploadStory(selectedFile, userLocation);
      
      toast({
        title: "Story Uploaded!",
        description: "Your story is being reviewed and will be live soon",
      });
      
      onUploadSuccess(story.id);
      handleClose();
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload story",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setValidationError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md mx-auto bg-carbon border-electric-violet">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-ice-white text-center">
            Upload Story
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Requirements Info */}
          <div className="bg-electric-violet/10 p-3 rounded-lg border border-electric-violet/30">
            <h4 className="text-sm font-semibold text-electric-violet mb-2">Video Requirements:</h4>
            <ul className="text-xs text-ice-white/70 space-y-1">
              <li>• Max 15 seconds duration</li>
              <li>• Vertical format (9:16 aspect ratio)</li>
              <li>• Max 1GB file size</li>
              <li>• MP4 or MOV format</li>
            </ul>
          </div>

          {/* File Upload */}
          {selectedFile ? (
            <div className="relative">
              <video
                src={URL.createObjectURL(selectedFile)}
                className="w-full h-64 object-cover rounded-lg bg-black"
                controls
                muted
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={() => setSelectedFile(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-electric-violet/30 rounded-lg p-8 text-center">
              <div className="space-y-2">
                <Camera className="w-8 h-8 text-electric-violet mx-auto" />
                <p className="text-ice-white/70">Select or record a video</p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-electric-violet text-electric-violet hover:bg-electric-violet/10"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Video
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* Validation Error */}
          {validationError && (
            <div className="flex items-center space-x-2 text-neon-red bg-neon-red/10 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">{validationError}</p>
            </div>
          )}

          {/* Location Warning */}
          {!userLocation && (
            <div className="flex items-center space-x-2 text-yellow-400 bg-yellow-400/10 p-3 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm">Location access required for geo-sorting</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-2 pt-4">
            <Button 
              variant="outline" 
              onClick={handleClose} 
              className="flex-1 border-electric-violet text-electric-violet"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!selectedFile || !userLocation || isUploading || !!validationError}
              className="flex-1 bg-electric-violet hover:bg-electric-violet/80"
            >
              {isUploading ? "Uploading..." : "Upload Story"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoryVideoUpload;
